import { useCallback, useEffect, useRef, useState } from 'react';
import { categorizeAsset } from './fileTypes';
import type { Asset, FilesApi, LayoutMode } from './types';

// Signed URLs expire after 60 minutes; refresh the whole set a little earlier.
const THUMBNAIL_REFRESH_MS = 50 * 60 * 1000;

interface UseThumbnailsOptions {
    patientId: string | undefined;
    assets: Record<string, Asset>;
    layoutMode: LayoutMode;
    api: FilesApi;
}

export interface UseThumbnailsResult {
    thumbnailUrls: Record<string, string>;
    /** Asset IDs whose current thumbnailUrls entry came from thumbUrl (not the
     *  full original) — the only cards where onThumbnailError should fire. */
    thumbUrlAssetIds: ReadonlySet<string>;
    /**
     * Called by FileCard's onError handler when a signed thumbUrl returns a 4xx.
     * Fetches the full original URL and swaps it into thumbnailUrls, then removes
     * the asset from thumbUrlAssetIds so subsequent errors fall through to
     * FileCard's own imageError state.
     */
    handleThumbnailError: (assetId: string) => Promise<void>;
}

/**
 * Grid-view thumbnails: prefers the signed thumbUrl already on GCS assets,
 * falls back to fetching the full download URL when absent, uses Monday public
 * URLs directly, and clears everything on a 50-minute timer so signed URLs
 * never go stale on screen.
 */
export function useThumbnails({
    patientId,
    assets,
    layoutMode,
    api
}: UseThumbnailsOptions): UseThumbnailsResult {
    const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
    const fetchedThumbnailKeys = useRef(new Set<string>());
    const [thumbUrlAssetIds, setThumbUrlAssetIds] = useState<ReadonlySet<string>>(new Set());
    const fetchingFallbackKeys = useRef(new Set<string>());

    useEffect(() => {
        if (layoutMode !== 'grid' || !patientId) return;
        const imageAssets = Object.values(assets).filter(
            (a) => categorizeAsset(a.name) === 'Images'
        );
        const immediateUrls: Record<string, string> = {};
        const newThumbKeys = new Set<string>();
        const gcsToFetch: Asset[] = [];
        for (const asset of imageAssets) {
            const key = String(asset.assetId);
            if (fetchedThumbnailKeys.current.has(key)) continue;
            if (asset.source === 'monday' && asset.publicUrl) {
                immediateUrls[key] = asset.publicUrl;
                fetchedThumbnailKeys.current.add(key);
            } else if (asset.source === 'gcs' && asset.patientFileId) {
                // Prefer the pre-generated thumbnail from the listing when available.
                if (asset.thumbUrl) {
                    immediateUrls[key] = asset.thumbUrl;
                    fetchedThumbnailKeys.current.add(key);
                    newThumbKeys.add(key);
                } else {
                    gcsToFetch.push(asset);
                }
            }
        }
        if (Object.keys(immediateUrls).length > 0) {
            setThumbnailUrls((prev) => ({ ...prev, ...immediateUrls }));
        }
        if (newThumbKeys.size > 0) {
            setThumbUrlAssetIds((prev) => {
                const next = new Set(prev);
                newThumbKeys.forEach((k) => next.add(k));
                return next;
            });
        }
        if (gcsToFetch.length === 0) return;
        let cancelled = false;
        const fetchGcsUrls = async () => {
            for (let i = 0; i < gcsToFetch.length; i += 3) {
                if (cancelled) return;
                const batch = gcsToFetch.slice(i, i + 3);
                const results = await Promise.allSettled(
                    batch.map(async (asset) => {
                        const url = await api.getDownloadUrl(patientId, asset.patientFileId!);
                        return { key: String(asset.assetId), url };
                    })
                );
                if (cancelled) return;
                const newUrls: Record<string, string> = {};
                for (const result of results) {
                    if (result.status === 'fulfilled') {
                        newUrls[result.value.key] = result.value.url;
                        fetchedThumbnailKeys.current.add(result.value.key);
                    }
                }
                if (Object.keys(newUrls).length > 0) {
                    setThumbnailUrls((prev) => ({ ...prev, ...newUrls }));
                }
            }
        };
        fetchGcsUrls();
        return () => {
            cancelled = true;
        };
    }, [assets, patientId, layoutMode, api]);

    // Periodic refresh so signed URLs on screen never outlive their expiry.
    useEffect(() => {
        if (layoutMode !== 'grid') return;
        const timer = setInterval(() => {
            fetchedThumbnailKeys.current.clear();
            fetchingFallbackKeys.current.clear();
            setThumbUrlAssetIds(new Set());
            setThumbnailUrls({});
        }, THUMBNAIL_REFRESH_MS);
        return () => clearInterval(timer);
    }, [layoutMode]);

    const handleThumbnailError = useCallback(
        async (assetId: string) => {
            if (!thumbUrlAssetIds.has(assetId)) return;
            if (fetchingFallbackKeys.current.has(assetId)) return;
            fetchingFallbackKeys.current.add(assetId);

            // Remove from thumbUrlAssetIds so the next render passes onThumbnailError=undefined
            // to this FileCard, letting its own imageError state handle any subsequent failure.
            setThumbUrlAssetIds((prev) => {
                const next = new Set(prev);
                next.delete(assetId);
                return next;
            });

            const asset =
                assets[assetId] ?? Object.values(assets).find((a) => String(a.assetId) === assetId);
            if (!asset?.patientFileId || !patientId) return;

            try {
                const url = await api.getDownloadUrl(patientId, asset.patientFileId);
                setThumbnailUrls((prev) => ({ ...prev, [assetId]: url }));
            } catch {
                // Original fetch also failed — remove the URL so FileCard gets no thumbnailUrl.
                // With onThumbnailError already absent (removed above), FileCard will show the icon.
                setThumbnailUrls((prev) => {
                    const next = { ...prev };
                    delete next[assetId];
                    return next;
                });
            }
        },
        [thumbUrlAssetIds, assets, patientId, api]
    );

    return {
        thumbnailUrls,
        thumbUrlAssetIds,
        handleThumbnailError
    };
}
